import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { API } from "../App";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";
const coverUrl = (v) => (v ? (/^https?:\/\//i.test(v) ? v : `${BACKEND_BASE}${v}`) : null);

// Homepage "Latest Guides" strip — SEO-friendly section that surfaces the blog
// on the main page and pushes visitors through to the individual article pages.
const BlogStripSection = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog?limit=3`)
      .then(r => setArticles(r.data.articles || []))
      .catch(() => setArticles([]));
  }, []);

  if (!articles.length) return null;

  return (
    <section id="guides" className="py-20 md:py-28 relative overflow-hidden" data-testid="blog-strip-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
        >
          <div>
            <div className="flex items-center gap-2 text-orange-400 text-sm uppercase tracking-widest mb-3">
              <BookOpen className="w-4 h-4" /> Saturn Journal
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black">
              Guides & <span className="text-orange-400">Know-How</span>
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl">
              Practical, no-nonsense guides on tinting, PPF, wraps and aftercare — written by the crew who fit them every day.
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold self-start md:self-end"
            data-testid="blog-strip-view-all"
          >
            All guides <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3">
          {articles.map((a, idx) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Link
                to={`/blog/${a.slug}`}
                className="group block bg-slate-900/60 border border-slate-800 hover:border-orange-500/50 rounded-2xl overflow-hidden h-full transition-all"
                data-testid={`blog-strip-card-${a.slug}`}
              >
                {coverUrl(a.cover_image) ? (
                  <div className="aspect-[16/9] w-full overflow-hidden bg-slate-800">
                    <img src={coverUrl(a.cover_image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] w-full bg-gradient-to-br from-orange-500/20 via-slate-800 to-slate-900 flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-orange-400/50" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(a.tags || []).slice(0, 1).map(t => (
                      <span key={t} className="text-[10px] uppercase tracking-wider text-orange-300/80 bg-orange-500/10 border border-orange-500/20 px-2 py-1 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-lg font-bold text-white group-hover:text-orange-300 mb-2 leading-snug">
                    {a.title}
                  </h3>
                  <p className="text-slate-400 text-sm line-clamp-3 mb-5">{a.excerpt}</p>
                  <div className="flex items-center gap-2 text-orange-400 text-sm font-semibold">
                    Read guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogStripSection;
